import { useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import Actionbar from "src/components/common/actionbar/Authactionbar";
import { Message } from "src/components/common/form/Message";
import useUiuxStore from "src/store/useUiuxStore";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { HRM3040_R01Content } from "./Content_HRM3040_R01";
import { HRM3040_R01ServiceAllowDto, HRM3040_R01PositionAllowDto, HRM3040_R01PreviewDto } from "./Dto_HRM3040_R01";
import { HRM3040_R01SearchSection } from "./SearchSection_HRM3040_R01";
import { useHRM3040_R01Store } from "./Store_HRM3040_R01";
import { ServiceTable_HRM3040_R01 } from "./ServiceTable_HRM3040_R01";
import { PositionTable_HRM3040_R01 } from "./PositionTable_HRM3040_R01";
import { PreviewTable_HRM3040_R01 } from "./PreviewTable_HRM3040_R01";

/** 좌우 기준표 너비 비율을 공통 리사이저로 저장하는 설정이다. */
const RESIZER = {
    code: 'HRM3040_R01', type: 'width', storageKey: 'HRM3040_R01-split-width',
    min: 5, max: 95, default: 50,
} as const;

/** 위쪽 기준표와 아래쪽 미리보기의 높이 비율을 브라우저에 저장하는 설정이다. */
const HEIGHT_RESIZER = {
    code: 'HRM3040_R01', type: 'height', storageKey: 'HRM3040_R01-split-height',
    min: 20, max: 80, default: 52,
} as const;

/** 기준 검색·두 편집 표·재직자 미리보기·액션바·메시지를 조립해 반환한다. 자동 업무 조회는 없다. */
export const Container_HRM3040_R01 = () => {

    const serviceGridRef = useRef<AgGridReact<HRM3040_R01ServiceAllowDto>>(null);
    const positionGridRef = useRef<AgGridReact<HRM3040_R01PositionAllowDto>>(null);
    const previewGridRef = useRef<AgGridReact<HRM3040_R01PreviewDto>>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const infoRef = useRef<HTMLDivElement>(null);
    const isResizing = useRef(false);
    const heightContainerRef = useRef<HTMLDivElement>(null);
    const heightInfoRef = useRef<HTMLDivElement>(null);
    const isHeightResizing = useRef(false);
    const { setMessage } = useUiuxStore();
    const { setActiveGridType } = useHRM3040_R01Store();
    const { splitValue, handleMouseDown } = useMouseContent({ resizer: RESIZER, isResizing, containerRef, infoRef });
    // HRM3050과 같은 공통 상하 분할 Hook에서 기준표 높이 비율과 드래그 시작 함수를 받는다.
    const { splitValue: topHeight, handleMouseDown: handleHeightMouseDown } = useMouseContent({
        resizer: HEIGHT_RESIZER, isResizing: isHeightResizing, containerRef: heightContainerRef, infoRef: heightInfoRef,
    });

    // Content에서 화면 버튼 함수와 각 표에 전달할 신규 행 기본값 함수를 받는다.
    const { clickable, setClickable, fetchData, defaultHRM3040_R01ServiceRow, defaultHRM3040_R01PositionRow,
        initContainerData, handleInsertClick, handleSaveClick, handleDeleteClick, handleExcelClick } =
        HRM3040_R01Content({ serviceGridRef, positionGridRef, previewGridRef, setMessage });

    return (
        <div className="content-container">
            {/* 조회·등록·삭제·저장·Excel·초기화를 연결한다. 닫기는 공통 액션바가 처리한다. */}
            <Actionbar onFetchClick={fetchData} onCreateClick={handleInsertClick} onDeleteClick={handleDeleteClick}
                onSaveClick={handleSaveClick} onExcelClick={handleExcelClick} onInitClick={initContainerData} />
            {/* 표시의 빈 값은 사용자 확정 문구인 '전체'이다. */}
            <HRM3040_R01SearchSection />
            {/* 기준표·미리보기 높이를 공통 리사이저로 조절한다. 최초 높이 비율은 기존과 같은 52%이다. */}
            <div className="flex flex-col flex-1 min-h-0 p-[12px] gap-[4px]" ref={heightContainerRef}>
                <div className="flex min-h-0 gap-[4px]" style={{ flex: `0 0 ${topHeight}%` }} ref={containerRef}>
                    {/* mousedown 때 Store를 바꾸면 + 버튼이 재생성되어 첫 클릭을 잃으므로 click 캡처에서 대상만 지정한다. */}
                    <div className="h-full min-w-0" style={{ width: `${splitValue}%` }} onClickCapture={() => setActiveGridType('service')}>
                        {/* 근속 기간·구간 편집 표이다. */}
                        <ServiceTable_HRM3040_R01 clickable={clickable} setClickable={setClickable} gridRef={serviceGridRef}
                            defaultHRM3040_R01ServiceRow={defaultHRM3040_R01ServiceRow()} handleInsertClick={handleInsertClick} />
                    </div>
                    {/* 기준 Table과 같은 좌우 너비 조절 리사이저이다. */}
                    <div className="w-[6px] shrink-0 bg-gray-100 cursor-col-resize hover:bg-gray-300"
                        onMouseDown={event => handleMouseDown('LR', event.nativeEvent)} />
                    <div className="h-full min-w-0 flex-1" ref={infoRef} onClickCapture={() => setActiveGridType('position')}>
                        {/* 직책 기간별 편집 표이다. */}
                        <PositionTable_HRM3040_R01 clickable={clickable} setClickable={setClickable} gridRef={positionGridRef}
                            defaultHRM3040_R01PositionRow={defaultHRM3040_R01PositionRow()} handleInsertClick={handleInsertClick} />
                    </div>
                </div>
                {/* 경계선을 위아래로 드래그하면 기준표와 미리보기의 높이를 함께 조절한다. */}
                <div className="h-[6px] shrink-0 bg-gray-100 cursor-row-resize hover:bg-gray-300"
                    title="적용 미리보기 영역 높이 조절"
                    onMouseDown={event => handleHeightMouseDown('UD', event.nativeEvent)} />
                {/* 미리보기는 남은 높이를 채우며 등록·삭제 대상 그리드를 변경하지 않는다. */}
                <div className="flex-1 min-h-0" ref={heightInfoRef}>
                    <PreviewTable_HRM3040_R01 gridRef={previewGridRef} />
                </div>
            </div>
            {/* 공통 조회·저장 안내 영역이다. */}
            <Message />
        </div>
    );
};
